import Popup from './_popup';

/**
 * ============================================================
 * Notices WooCommerce en popup
 * ============================================================
 *
 * Intercepte toutes les notices WooCommerce insérées dans
 * .woocommerce-notices-wrapper (chargement de page + AJAX)
 * et les affiche dans la popup [data-popup="wc-notices"].
 *
 * SYSTÈME D'EXTRAS
 * ----------------
 * Permet d'injecter du contenu supplémentaire après n'importe
 * quelle notice, selon une condition sur l'élément notice.
 *
 * Enregistrement depuis n'importe quel autre module :
 *
 *   import { wcNoticesPopup } from './_wc-notices-popup';
 *
 *   wcNoticesPopup.addExtra({
 *     // Condition : reçoit le nœud notice original (avant clonage)
 *     match : (noticeEl) => noticeEl.classList.contains('woocommerce-error'),
 *     // Rendu : reçoit le clone inséré dans la popup, doit retourner un HTMLElement
 *     render: (noticeEl) => {
 *       const el = document.createElement('p');
 *       el.textContent = 'Texte supplémentaire';
 *       return el;
 *     },
 *   });
 * ============================================================
 */

/** @type {Array<{ match: (notice: HTMLElement) => boolean, render: (notice: HTMLElement) => HTMLElement }>} */
const extras = [];

/**
 * Enregistre un extra conditionnel à afficher après une notice.
 * @param {{ match: function, render: function }} config
 */
function addExtra(config) {
    extras.push(config);
}

/**
 * Applique les extras enregistrés sur la notice clonée.
 * @param {HTMLElement} clone    - Nœud inséré dans la popup
 * @param {HTMLElement} original - Nœud source d'origine
 */
function applyExtras(clone, original) {
    extras.forEach(({ match, render }) => {
        if (!match(original)) return;
        const extra = render(original);
        if (extra instanceof HTMLElement) {
            clone.after(extra);
        }
    });
}

/**
 * Déplace les notices WooCommerce actives dans la popup et l'ouvre.
 * Réinitialise le contenu à chaque appel pour éviter les doublons.
 */
function moveNoticesToPopup() {
    const notices = document.querySelectorAll(
        '.woocommerce-notices-wrapper .woocommerce-message,' +
        '.woocommerce-notices-wrapper .woocommerce-error,' +
        '.woocommerce-notices-wrapper .woocommerce-info'
    );
    if (!notices.length) return;

    const popupContent = document.querySelector('.wc-notices-popup__content');
    if (!popupContent) return;

    popupContent.innerHTML = '';

    notices.forEach(original => {
        const clone = original.cloneNode(true);
        popupContent.appendChild(clone);
        applyExtras(clone, original);
        original.remove();
    });

    Popup.open('wc-notices');
}

document.addEventListener('DOMContentLoaded', () => {
    const popupEl = document.querySelector('[data-popup="wc-notices"]');

    // --- Extra par défaut : boutons panier sur les notices d'ajout au panier ---
    // WooCommerce identifie ces notices par la présence d'un lien .wc-forward
    addExtra({
        match: (notice) =>
            notice.classList.contains('woocommerce-message') &&
            notice.querySelector('a.wc-forward, a.button') !== null,

        render: () => {
            const cartUrl       = popupEl?.dataset.cartUrl      ?? '#';
            const labelContinue = popupEl?.dataset.labelContinue ?? 'Continuer mes achats';
            const labelCart     = popupEl?.dataset.labelViewCart ?? 'Voir mon panier';

            const wrap = document.createElement('div');
            wrap.className = 'wc-notices-popup__actions';

            const continueBtn = document.createElement('button');
            continueBtn.type = 'button';
            continueBtn.className = 'wc-notices-popup__btn wc-notices-popup__btn--secondary';
            continueBtn.textContent = labelContinue;
            continueBtn.addEventListener('click', () => Popup.close('wc-notices'));

            const cartLink = document.createElement('a');
            cartLink.href = cartUrl;
            cartLink.className = 'button wc-notices-popup__btn wc-notices-popup__btn--primary';
            cartLink.textContent = labelCart;

            wrap.appendChild(continueBtn);
            wrap.appendChild(cartLink);
            return wrap;
        },
    });

    // --- Extra : bouton "continuer mes achats" sur la notice de panier vidé ---
    // WooCommerce utilise woocommerce-info + cart-empty pour cette notice
    addExtra({
        match: (notice) => notice.classList.contains('cart-empty'),

        render: () => {
            const labelContinue = popupEl?.dataset.labelContinue ?? 'Continuer mes achats';

            const wrap = document.createElement('div');
            wrap.className = 'wc-notices-popup__actions';

            const continueBtn = document.createElement('button');
            continueBtn.type = 'button';
            continueBtn.className = 'wc-notices-popup__btn wc-notices-popup__btn--secondary';
            continueBtn.textContent = labelContinue;
            continueBtn.addEventListener('click', () => Popup.close('wc-notices'));

            wrap.appendChild(continueBtn);
            return wrap;
        },
    });

    // Notices présentes dès le chargement de la page
    moveNoticesToPopup();

    // Notices injectées dynamiquement via AJAX (ajout au panier, etc.)
    const observer = new MutationObserver(moveNoticesToPopup);
    observer.observe(document.body, { childList: true, subtree: true });
});

export const wcNoticesPopup = { addExtra, moveNoticesToPopup };